import { ArtistMediaCountDoc } from '@minecraft.graphql-operations';
import { useQuery } from '@apollo/client';
import { UseArtistEntitlement, useArtistEntitlement } from '@blocs.media-management';
import { gqlMediaTypeCodeEnum, gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { useArtistContext } from './ArtistContext';

type MediaCreditsRemaining = {
  photoUploadRemaining: number;
  videoUploadRemaining: number;
  audioUploadRemaining: number;
};

export type UseCreditsUI = {
  loading: boolean;
  userHasCreditsToRedeem: boolean;
  priorityMediaToRedeem: string;
  availableCreditsByType: UseArtistEntitlement['availableCreditsByType'];
};

const determineMediaToRedeem = ({
  photoUploadRemaining,
  videoUploadRemaining,
  audioUploadRemaining,
}: MediaCreditsRemaining): MEDIA_TYPE => {
  // Define priority for media types
  const priority = [MEDIA_TYPE.PHOTO, MEDIA_TYPE.VIDEO, MEDIA_TYPE.AUDIO];

  // Check for remaining credits and prioritize based on the defined order
  const remainingMedia = priority.find((mediaType) => {
    switch (mediaType) {
      case MEDIA_TYPE.PHOTO:
        return photoUploadRemaining > 0;
      case MEDIA_TYPE.VIDEO:
        return videoUploadRemaining > 0;
      case MEDIA_TYPE.AUDIO:
        return audioUploadRemaining > 0;
      default:
        return false;
    }
  });

  // Default to PHOTO if no credits remaining or priority match found
  return remainingMedia || MEDIA_TYPE.PHOTO;
};

export const useCreditsUI = ({ artistId }: { artistId: number }): UseCreditsUI => {
  const { isArtistLocked } = useArtistContext();

  const { data, loading, error } = useQuery(ArtistMediaCountDoc, {
    variables: { artistId },
    skip: !artistId,
  });

  const artist = data?.searchArtistMedia?.artist;
  const { hasMediaCreditsAvailable, availableCreditsByType, entitlementsByType } = useArtistEntitlement({ artist });
  const isUnlimited = !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.PHOTO]);
  const hasMedia = (data?.searchArtistMedia.totalCount ?? 0) > 0;

  if (loading || error || !data || isUnlimited) {
    return {
      loading,
      userHasCreditsToRedeem: false,
      priorityMediaToRedeem: '',
      availableCreditsByType,
    };
  }

  const priorityMediaToRedeem = determineMediaToRedeem({
    photoUploadRemaining: availableCreditsByType[gqlMediaTypeCodeEnum.PHOTO],
    videoUploadRemaining: availableCreditsByType[gqlMediaTypeCodeEnum.VIDEO],
    audioUploadRemaining: availableCreditsByType[gqlMediaTypeCodeEnum.AUDIO],
  }).toLowerCase();

  return {
    loading: false,
    userHasCreditsToRedeem: !isUnlimited && hasMediaCreditsAvailable && hasMedia && !isArtistLocked,
    priorityMediaToRedeem,
    availableCreditsByType,
  };
};
